import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { CheckCircle, XCircle, Clock, Loader2, Calendar, MapPin, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { formatEventDateTime } from '../utils/dateFormat';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MasterCheckIn() {
  const { host_id } = useParams();
  const [email, setEmail] = useState('');
  const [step, setStep] = useState('email'); // email | loading | select | checkin-loading | success | already | error
  const [events, setEvents] = useState([]);
  const [hostName, setHostName] = useState('');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [result, setResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  const handleLookup = async (e) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) return;
    setStep('loading');
    try {
      const res = await axios.post(`${API}/hosts/${host_id}/master-checkin-lookup`, { email: trimmed });
      setHostName(res.data.host_name);
      const eligible = res.data.events;
      setEvents(eligible);

      // If exactly one valid (not checked in, window ok), go straight to check-in
      const checkable = eligible.filter(ev => !ev.already_checked_in && ev.window_ok);
      if (checkable.length === 1) {
        doCheckIn(checkable[0]);
      } else {
        setStep('select');
      }
    } catch (err) {
      setErrorMsg(err.response?.data?.detail || 'Something went wrong');
      setStep('error');
    }
  };

  const doCheckIn = async (ev) => {
    setSelectedEvent(ev);
    if (ev.already_checked_in) {
      setResult({ status: 'already_checked_in', guest_name: ev.guest_name, event_title: ev.title, scanned_at: ev.scanned_at });
      setStep('already');
      return;
    }
    if (!ev.window_ok) {
      setErrorMsg('Check-in window is not active for this event.');
      setStep('error');
      return;
    }
    setStep('checkin-loading');
    try {
      const res = await axios.post(`${API}/events/${ev.event_id}/self-checkin`, { email: email.trim().toLowerCase() });
      setResult(res.data);
      setStep(res.data.status === 'already_checked_in' ? 'already' : 'success');
    } catch (err) {
      setErrorMsg(err.response?.data?.detail || 'Check-in failed');
      setStep('error');
    }
  };

  const reset = () => {
    setStep('email');
    setEmail('');
    setEvents([]);
    setSelectedEvent(null);
    setResult(null);
    setErrorMsg('');
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4" data-testid="master-checkin-page">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] text-xs font-medium tracking-wider uppercase mb-4">
            Comp Guest Check-In
          </div>
          {hostName && <p className="text-sm text-[#777]">Hosted by {hostName}</p>}
        </div>

        {/* Email Step */}
        {step === 'email' && (
          <form onSubmit={handleLookup} className="space-y-4" data-testid="master-email-form">
            <div>
              <label className="block text-sm text-[#AFAFAF] mb-2">Enter your email to check in</label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                autoFocus
                className="bg-[#0A0A0A] border-[#1A1A1A] text-[#EAE7E1] placeholder:text-[#555] rounded-sm h-12 text-base"
                data-testid="master-email-input"
              />
            </div>
            <Button
              type="submit"
              className="w-full h-12 bg-gradient-to-r from-[#8C6B1F] via-[#D4AF37] to-[#F2D27A] hover:opacity-90 text-[#050505] rounded-sm font-bold text-base"
              data-testid="master-lookup-button"
            >
              Find My Events
            </Button>
          </form>
        )}

        {/* Loading */}
        {(step === 'loading' || step === 'checkin-loading') && (
          <div className="text-center py-12" data-testid="master-loading">
            <Loader2 className="w-10 h-10 text-[#D4AF37] animate-spin mx-auto mb-4" />
            <p className="text-[#AFAFAF]">{step === 'loading' ? 'Looking up events...' : 'Checking in...'}</p>
          </div>
        )}

        {/* Event Selection */}
        {step === 'select' && (
          <div className="space-y-3" data-testid="master-event-list">
            <p className="text-sm text-[#AFAFAF] mb-4">
              {events.filter(ev => !ev.already_checked_in && ev.window_ok).length > 0
                ? 'Select the event to check into:'
                : 'Your comp tickets:'}
            </p>
            {events.map(ev => {
              const canCheck = !ev.already_checked_in && ev.window_ok;
              return (
                <button
                  key={ev.event_id}
                  onClick={() => doCheckIn(ev)}
                  className={`w-full p-4 rounded-sm border text-left transition-colors ${
                    ev.already_checked_in
                      ? 'border-emerald-500/20 bg-emerald-500/5'
                      : !ev.window_ok
                      ? 'border-[#1A1A1A] opacity-50'
                      : 'border-[#1A1A1A] hover:border-[#D4AF37]/30 hover:bg-[#D4AF37]/5'
                  }`}
                  data-testid={`master-event-${ev.event_id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-[#EAE7E1]">{ev.title}</div>
                      <div className="text-xs text-[#777] mt-1 flex items-center gap-1">
                        <Calendar className="w-3 h-3" /> {formatEventDateTime(ev.date, ev.time)}
                      </div>
                      {ev.location && (
                        <div className="text-xs text-[#555] mt-0.5 flex items-center gap-1">
                          <MapPin className="w-3 h-3" /> {ev.location}
                        </div>
                      )}
                      <div className="text-xs text-[#555] mt-1">{ev.pass_tier_name} &middot; {ev.guest_name}</div>
                    </div>
                    <div className="flex-shrink-0">
                      {ev.already_checked_in ? (
                        <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400">Checked In</span>
                      ) : !ev.window_ok ? (
                        <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400">Window Closed</span>
                      ) : (
                        <ChevronRight className="w-5 h-5 text-[#D4AF37]" />
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
            <Button onClick={reset} variant="outline" className="w-full mt-4 border-[#1A1A1A] text-[#AFAFAF] hover:text-[#EAE7E1] rounded-sm" data-testid="master-back-button">
              Back
            </Button>
          </div>
        )}

        {/* Success */}
        {step === 'success' && result && (
          <div className="text-center py-8 space-y-4" data-testid="master-success">
            <div className="w-20 h-20 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto">
              <CheckCircle className="w-12 h-12 text-emerald-400" />
            </div>
            <h2 className="text-2xl font-bold text-[#EAE7E1]">You're checked in!</h2>
            <p className="text-[#AFAFAF]">Welcome, {result.guest_name}</p>
            {result.event_title && <p className="text-sm text-[#777]">{result.event_title}</p>}
          </div>
        )}

        {/* Already checked in */}
        {step === 'already' && (
          <div className="text-center py-8 space-y-4" data-testid="master-already">
            <div className="w-20 h-20 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mx-auto">
              <CheckCircle className="w-12 h-12 text-[#D4AF37]" />
            </div>
            <h2 className="text-2xl font-bold text-[#EAE7E1]">Already checked in</h2>
            <p className="text-[#AFAFAF]">Welcome back, {result?.guest_name || selectedEvent?.guest_name}</p>
            {(result?.event_title || selectedEvent?.title) && <p className="text-sm text-[#777]">{result?.event_title || selectedEvent?.title}</p>}
            <Button onClick={reset} variant="outline" className="border-[#1A1A1A] text-[#AFAFAF] hover:text-[#EAE7E1] rounded-sm mt-4" data-testid="master-back-button">
              Check in for another event
            </Button>
          </div>
        )}

        {/* Error */}
        {step === 'error' && (
          <div className="text-center py-8 space-y-4" data-testid="master-error">
            <div className="w-20 h-20 rounded-full bg-red-500/10 flex items-center justify-center mx-auto">
              {errorMsg.includes('not started') || errorMsg.includes('closed') || errorMsg.includes('window') ? (
                <Clock className="w-12 h-12 text-amber-400" />
              ) : (
                <XCircle className="w-12 h-12 text-red-400" />
              )}
            </div>
            <h2 className="text-xl font-bold text-[#EAE7E1]">
              {errorMsg.includes('No active comp') ? 'Not on the guest list' :
               errorMsg.includes('window') ? 'Check-in window closed' : 'Unable to check in'}
            </h2>
            <p className="text-[#AFAFAF] text-sm">{errorMsg}</p>
            <Button onClick={reset} variant="outline" className="border-[#1A1A1A] text-[#AFAFAF] hover:text-[#EAE7E1] hover:bg-[#1A1A1A] rounded-sm mt-4" data-testid="master-try-again">
              Try again
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
